import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { Navigate } from 'react-router-dom';
import { useGetClientAdminsPartnerFeatureFlags } from '~/hooks';
import { isApiIntegration } from '~/utils/isApiIntegration';
import { ROUTES } from '~/utils/routes';

type RootRedirectInnerProps = {
  threeLetterCode: string;
};

/**
 * threeLetterCode が確定している場合にパートナー機能フラグを取得し、
 * API 連携パートナーかどうかで ROOT の着地先を振り分ける。
 *
 * threeLetterCode を持つ場合のみ描画されるコンポーネントとして分離することで、
 * フラグ取得 hook を条件付きで呼び出さずに済ませている（Rules of Hooks 対策）。
 *
 * `useGetClientAdminsPartnerFeatureFlags` は suspense 前提のため、
 * このコンポーネントを描画する側（screens/Root）が Suspense 境界を張ること。
 */
const RootRedirectInner: React.FC<RootRedirectInnerProps> = ({ threeLetterCode }) => {
  const { data } = useGetClientAdminsPartnerFeatureFlags({ threeLetterCode });

  return (
    <Navigate
      to={isApiIntegration(data.partner_feature_flags) ? ROUTES.PENDING_USERS : ROUTES.USERS}
      replace
    />
  );
};

/**
 * ログイン後の ROOT（`/`）着地先を、パートナーの API 連携機能フラグに応じて振り分けるガード型 feature。
 *
 * - API 連携パートナー: `/pending-users`（初回ログイン未完了者一覧）
 * - それ以外: `/users`（利用者一覧）
 *
 * 表示を持たない判定専用のコンポーネントのため、Presentation / logics は設けない
 * （`components/auth/StressAssessmentRoleRoute` と同型のガード型 feature）。
 *
 * threeLetterCode が未確定の場合はフラグを判定できず、かつ `/users` を含む全ての
 * ClientAdmins API は threeLetterCode 必須のため `/users` へフォールバックしても
 * API が失敗し画面が真っ白になる。未確定＝未ログイン相当とみなし `/login` へ戻す
 * （threeLetterCode が空だと suspense hook のキーが安定せずフェッチが固着してしまうため、
 * hook を呼ぶ前に弾く）。
 */
export const RootRedirect: React.FC = () => {
  const threeLetterCode = getThreeLetterCode();
  if (!threeLetterCode) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return <RootRedirectInner threeLetterCode={threeLetterCode} />;
};
